<script setup>
import { ref, computed, onMounted } from 'vue';
import { toast } from 'dashboard/composables/useToast';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CrmDataTable from '../../../crm/components/reports/CrmDataTable.vue';
import AppSmartLoader from '../../../crm/components/AppSmartLoader.vue';
import CampaignAliasesAPI from 'dashboard/api/campaignAliases';

const records = ref([]);
const targets = ref([]);
const sources = ref([]);
const isFetching = ref(false);
const isSaving = ref(false);
const confirmDeleteId = ref(null);

const form = ref({ source_utm: '', target_key: '' });

const fetchAll = async () => {
  isFetching.value = true;
  try {
    const [list, opts] = await Promise.all([
      CampaignAliasesAPI.get(),
      CampaignAliasesAPI.getTargets(),
    ]);
    records.value = list.data || [];
    targets.value = opts.data.targets || [];
    sources.value = opts.data.sources || [];
  } catch (error) {
    // Silencioso — tela vazia é estado válido.
  } finally {
    isFetching.value = false;
  }
};

onMounted(fetchAll);

const usedSources = computed(() => records.value.map(r => r.source_utm));
const availableSources = computed(() =>
  sources.value.filter(s => !usedSources.value.includes(s))
);

const isValid = computed(
  () => form.value.source_utm.trim() && form.value.target_key.trim()
);

const tableColumns = [
  { key: 'source_utm', label: 'UTM do lead', align: 'left' },
  { key: 'arrow', label: '', align: 'center' },
  { key: 'target_key', label: 'Campanha real (coletada)', align: 'left' },
  { key: 'actions', label: '', align: 'right' },
];

const onCreate = async () => {
  if (!isValid.value) return;
  isSaving.value = true;
  try {
    await CampaignAliasesAPI.create({ campaign_alias: { ...form.value } });
    form.value = { source_utm: '', target_key: '' };
    toast.success('Alias criado.');
    await fetchAll();
  } catch (error) {
    toast.error('Não foi possível criar (UTM já mapeada?).');
  } finally {
    isSaving.value = false;
  }
};

const onDelete = async row => {
  if (confirmDeleteId.value === row.id) {
    await CampaignAliasesAPI.delete(row.id);
    confirmDeleteId.value = null;
    await fetchAll();
  } else {
    confirmDeleteId.value = row.id;
    setTimeout(() => {
      confirmDeleteId.value = null;
    }, 3000);
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="flex flex-col w-full max-w-[60rem] gap-6 mx-auto">
    <div class="min-w-0">
      <h1 class="text-xl font-medium text-n-slate-12">Alias de campanha</h1>
      <p class="mt-1 text-sm text-n-slate-10">
        Use quando a UTM que chega nos leads é diferente do nome da campanha no
        Google/Meta. O alias liga a UTM à campanha real coletada, para o gasto
        cruzar corretamente em Relatórios → Marketing → Performance.
      </p>
    </div>

    <!-- Add form -->
    <div
      class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-alpha-black2"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label class="block text-xs font-medium text-n-slate-10 mb-1">
            UTM do lead
          </label>
          <input
            v-model="form.source_utm"
            list="alias-sources"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:ring-1 focus:ring-woot-500"
            placeholder="Digite ou escolha a UTM"
          />
          <datalist id="alias-sources">
            <option v-for="s in availableSources" :key="s" :value="s" />
          </datalist>
        </div>
        <div>
          <label class="block text-xs font-medium text-n-slate-10 mb-1">
            Campanha real (coletada)
          </label>
          <input
            v-model="form.target_key"
            list="alias-targets"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:ring-1 focus:ring-woot-500"
            placeholder="Nome da campanha no Google/Meta"
          />
          <datalist id="alias-targets">
            <option v-for="t in targets" :key="t" :value="t" />
          </datalist>
        </div>
      </div>
      <div class="flex justify-end">
        <NextButton
          icon="i-lucide-link"
          label="Criar alias"
          sm
          :is-loading="isSaving"
          :disabled="!isValid"
          @click="onCreate"
        />
      </div>
    </div>

    <div
      v-if="isFetching && !records.length"
      class="flex items-center justify-center h-40"
    >
      <AppSmartLoader animation="radar" :size="48" />
    </div>

    <div
      v-else-if="!records.length"
      class="flex flex-col items-center justify-center gap-2 p-8 rounded-xl text-n-slate-11 bg-n-alpha-black2"
    >
      <span class="i-lucide-link-2 size-8 text-n-slate-8" />
      <span class="text-sm">Nenhum alias cadastrado.</span>
    </div>

    <CrmDataTable
      v-else
      :columns="tableColumns"
      :rows="records"
      export-filename="alias_campanhas"
    >
      <template #arrow>
        <span class="i-lucide-arrow-right size-4 text-n-slate-9" />
      </template>
      <template #actions="{ row }">
        <button
          type="button"
          class="flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors"
          :class="
            confirmDeleteId === row.id
              ? 'text-white bg-red-500'
              : 'text-n-slate-10 hover:text-red-500 hover:bg-red-500/10'
          "
          @click="onDelete(row)"
        >
          <span class="i-lucide-trash-2 size-3" />
          {{ confirmDeleteId === row.id ? 'Confirmar?' : 'Excluir' }}
        </button>
      </template>
    </CrmDataTable>
  </div>
</template>
